import { observable, action, computed, makeObservable, reaction } from 'mobx';
import { PackageFieldResult } from '../../common/services/types';
import { ProjectsService } from '../../common/services';
import { DocumentVisualStore } from '../../documents/stores';
import { Disposer, DocumentHelper, DocumentFieldFinder } from '../misc';
import { IAppStoreBase, IInputsStoreBase } from '../types';

type PreviewPosition = 'rightSide' | 'fullScreen';

type BehaviorOptions = {
    hideMinimapOnRightSidePreview?: boolean;
};

export default class DocumentPreviewStore extends Disposer {
    static readonly noResultsFoundMessage = 'No results found for your search';

    expanded: boolean = true;

    showMinimap: boolean = true;

    previewPosition: PreviewPosition = 'rightSide';

    searchTerm: string = '';

    searchInputVisible: boolean = false;

    fieldSearchResults: PackageFieldResult[] = [];

    fieldSearchBlockPages: number[] | undefined;

    noResultsFound: boolean = false;

    constructor(
        private readonly appStore: IAppStoreBase,
        private readonly inputsStore: IInputsStoreBase,
        private readonly documentVisualStore: DocumentVisualStore,
        private readonly projectsService: ProjectsService,
        readonly behaviorOptions: BehaviorOptions = {}
    ) {
        super();

        makeObservable(this, {
            expanded: observable,
            showMinimap: observable,
            previewPosition: observable,
            searchTerm: observable,
            searchInputVisible: observable,
            fieldSearchResults: observable,
            fieldSearchBlockPages: observable,
            noResultsFound: observable,
            setExpanded: action.bound,
            setShowMinimap: action.bound,
            setPreviewPosition: action.bound,
            setSearchTerm: action.bound,
            setSearchInputVisible: action.bound,
            setFieldSearchResults: action.bound,
            setFieldSearchBlockPages: action.bound,
            setNoResultsFound: action.bound,
            fullScreenMode: computed,
            pageFilter: computed,
            visibleBlocks: computed,
            documentBlocks: computed,
            fieldSearchBlocks: computed,
            showMinimapToggleButton: computed,
            showNoResultsMessage: computed
        });

        this.reactions.push(
            reaction(
                () => this.searchInputVisible,
                searchInputVisible => {
                    this.documentVisualStore.highlightBlock(undefined, undefined);

                    if (!searchInputVisible) {
                        this.setSearchTerm('');
                        this.setFieldSearchResults([]);
                    }
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.visibleBlocks,
                visibleBlocks => {
                    this.documentVisualStore.setDocumentBlocks(visibleBlocks);

                    if (this.searchInputVisible && visibleBlocks.length) {
                        const firstField = this.visibleBlocks[0];
                        this.documentVisualStore.scrollAndHighlightBlock(firstField.id);
                    }
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.pageFilter,
                pageFilter => this.documentVisualStore.setPagesFilter(pageFilter)
            )
        );

        if (this.behaviorOptions.hideMinimapOnRightSidePreview) {
            this.setShowMinimap(false);
        }
    }

    setExpanded(expanded: boolean) {
        this.expanded = expanded;
    }

    setShowMinimap(showMinimap: boolean) {
        this.showMinimap = showMinimap;
    }

    setPreviewPosition(previewPosition: PreviewPosition) {
        this.previewPosition = previewPosition;
    }

    setSearchTerm(searchTerm: string) {
        this.searchTerm = searchTerm;
    }

    setSearchInputVisible(searchInputVisible: boolean) {
        this.searchInputVisible = searchInputVisible;
    }

    setFieldSearchResults(fieldSearchResults: PackageFieldResult[]) {
        this.fieldSearchResults = fieldSearchResults;
    }

    setFieldSearchBlockPages(fieldSearchBlockPages: number[] | undefined) {
        this.fieldSearchBlockPages = fieldSearchBlockPages;
    }

    setNoResultsFound(noResultsFound: boolean) {
        this.noResultsFound = noResultsFound;
    }

    async searchPackageFields() {
        const searchTerm = this.searchTerm.trim();

        if (searchTerm.length < 3) {
            this.setNoResultsFound(false);
            this.setFieldSearchResults([]);
            this.setFieldSearchBlockPages(undefined);
            return;
        }

        const linesBlocks = await this.projectsService.getPackageLines({
            projectId: this.appStore.projectId,
            packageId: this.appStore.packageId,
            searchTerm,
            pageSize: 999999,
            page: 0,
            fieldsSearch: {
                before: { blockType: 'HORIZONTAL_MULTILINE_BLOCK' }
            },
            searchOptions: {
                childFieldFilter: {
                    blockType: 'WORD_BLOCK'
                },
                textSearch: {
                    ignoreNewLines: true
                }
            }
        });

        if (!linesBlocks || !linesBlocks.result.length) {
            this.setNoResultsFound(true);
            this.setFieldSearchResults([]);
            this.setFieldSearchBlockPages([]);
            return;
        }

        this.setNoResultsFound(false);
        this.setFieldSearchResults(DocumentFieldFinder.searchChildFields(searchTerm, linesBlocks.result));
        this.setFieldSearchBlockPages([...new Set(this.fieldSearchResults.map(block => block.page))]);
    }

    get fullScreenMode() {
        return this.previewPosition === 'fullScreen';
    }

    get pageFilter() {
        return this.searchInputVisible ? this.fieldSearchBlockPages : undefined;
    }

    get visibleBlocks() {
        return this.searchInputVisible ? this.fieldSearchBlocks : this.documentBlocks;
    }

    get documentBlocks() {
        if (!this.documentVisualStore.pageWidth) {
            return [];
        }

        return DocumentHelper.prepareDocumentBlocks(
            this.inputsStore.inputs,
            this.documentVisualStore.pageWidth,
            this.appStore.packageId,
            'guid'
        );
    }

    get fieldSearchBlocks() {
        if (!this.documentVisualStore.pageWidth) {
            return [];
        }

        return DocumentHelper.preparePackageFieldBlocks(
            this.fieldSearchResults,
            this.documentVisualStore.pageWidth,
            this.appStore.packageId
        );
    }

    get showMinimapToggleButton() {
        return !this.behaviorOptions.hideMinimapOnRightSidePreview;
    }

    get showNoResultsMessage() {
        return this.searchInputVisible && this.noResultsFound;
    }

    dispose() {
        this.disposeReactions();
    }
}
